import { ControlValueAccessor } from '@angular/forms';

/**
 * Base class implementing the ControlValueAccessor interface.
 * Provides a reusable implementation for handling custom form controls.
 */
export abstract class ControlValueAccessorBase<T> implements ControlValueAccessor {
	/**
	 * Internal value of the control.
	 * This is the data model linked to the control.
	 */
	private innerValue!: T;

	/**
	 * References to Angular's control functions.
	 * These are set via the `registerOnChange` and `registerOnTouched` methods.
	 */
	/* istanbul ignore next */
	protected onChange: (value: T) => void = () => undefined;
	/* istanbul ignore next */
	protected onTouched: () => void = () => undefined;

	/**
	 * Getter for the control's value.
	 * @returns The current value of the control.
	 */
	get value(): T {
		return this.innerValue;
	}

	/**
	 * Setter for the control's value.
	 * Updates the internal value and notifies Angular of the change.
	 * @param value The new value to set.
	 */
	set value(value: T) {
		if (value !== this.innerValue) {
			this.innerValue = value;
			this.notifyValueChange(value); // Notify Angular of the value change
		}
	}

	/**
	 * Writes a new value to the control.
	 * This method is called by Angular when initializing the control or updating its value.
	 * @param value The new value to write.
	 */
	writeValue(value: T): void {
		this.innerValue = value;
	}

	/**
	 * Registers a callback function to handle value changes.
	 * This method is called by Angular to connect the custom control to the form control.
	 * @param fn The function to execute when the value changes.
	 */
	registerOnChange(fn: (value: T) => void): void {
		this.onChange = fn;
	}

	/**
	 * Registers a callback function to handle "touched" events.
	 * This method is called by Angular when the control is interacted with.
	 * @param fn The function to execute when the control is touched.
	 */
	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	/**
	 * Marks the control as "touched."
	 * This method is useful for triggering validation when the control is interacted with.
	 */
	markAsTouched(): void {
		if (this.onTouched) {
			this.onTouched();
		}
	}

	/**
	 * Notifies Angular of changes to the control's value.
	 * This ensures that the form's state stays in sync with the control.
	 * @param value The new value to notify.
	 */
	protected notifyValueChange(value: T): void {
		if (this.onChange) {
			this.onChange(value);
		}
	}

	/**
	 * Sets the disabled state of the control.
	 * This method is called by Angular when the control's disabled state changes.
	 * Subclasses should override this if they need custom disabled handling.
	 * @param isDisabled Whether the control should be disabled.
	 */
	setDisabledState?(isDisabled: boolean): void;
}
